// deps
import React, { Dispatch, SetStateAction, memo, useCallback, useContext, useEffect, useState } from "react";

// analytics
import { isEmpty } from "@swiggy-private/js-utils";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import { AnalyticsContext } from "@swiggy-private/analytics";

// internal deps
import {
    ApplyCouponInputBox,
    CouponApplyingBottomSheet,
    FaqContainer,
    MastHead,
} from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/Home";

// constants
import { HOME_TEST_IDS } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/Home";
import {
    clickFaqButtonEvent,
    clickTncButtonEvent,
    impressionHomeMastHead,
    impressionCouponCodeInput,
    clickCouponCodeInput,
    clickApplyButtonEvent,
    impressionFaqButtonEvent,
    impressionTncButtonEvent,
    impressionApplyingCouponLoaderEvent,
} from "analytics";

// constant
import { FAQ_URL, TNC_URL } from "constants/links";
import { APPLY_COUPON_BTN, HOME_SUBTITLE, HOME_TITLE, INPUT_BOX_EMPTY_ERROR_MSG } from "constants/texts";

// styles
import { ApplyCouponButton, Content, Heading, SubHeading, TopContentContainer, Seperator } from "styles/home";
// utils
import { getBridgeSdk } from "utils/bridgeSdk";

interface IProps {
    onBackButtonClick: () => void;
    couponCodeParams: string;
    applyCouponOnClick: (couponCode: string) => void;
    inputBoxError: string;
    setInputBoxError: Dispatch<SetStateAction<string>>;
    showBottomSheet: boolean;
}

const SuccessContentHomePage = ({
    onBackButtonClick,
    couponCodeParams,
    applyCouponOnClick,
    inputBoxError,
    setInputBoxError,
    showBottomSheet,
}: IProps) => {
    const [couponCode, setCouponCode] = useState<string>(couponCodeParams || "");
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    useEffect(() => {
        setCouponCode(couponCodeParams || couponCode || "");
    }, [couponCodeParams, couponCode]);

    const impressionEventForMastHead = useCallback(() => {
        impressionHomeMastHead?.(impressionEvent);
    }, [impressionEvent]);

    const impressionEventForApplyingBottomsheetLoader = useCallback(() => {
        impressionApplyingCouponLoaderEvent?.(impressionEvent);
    }, [impressionEvent]);

    const applyCouponCodeButtonClick = useCallback(() => {
        clickApplyButtonEvent?.(clickEvent, couponCode);
        if (!isEmpty(couponCode)) {
            applyCouponOnClick?.(couponCode);
        } else {
            setInputBoxError(INPUT_BOX_EMPTY_ERROR_MSG);
        }
    }, [applyCouponOnClick, clickEvent, couponCode, setInputBoxError]);

    const onFaqClick = useCallback(() => {
        clickFaqButtonEvent?.(clickEvent);
        getBridgeSdk()
            .navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: FAQ_URL,
            })
            .catch(() => void 0);
    }, [clickEvent]);

    const onTncClick = useCallback(() => {
        clickTncButtonEvent?.(clickEvent);
        getBridgeSdk()
            .navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: TNC_URL,
            })
            .catch(() => void 0);
    }, [clickEvent]);

    const impressionCouponCodeInputAnalytics = useCallback(() => {
        impressionCouponCodeInput?.(impressionEvent);
    }, [impressionEvent]);

    const clickCouponCodeInputAnalytics = useCallback(
        (code: string) => {
            clickCouponCodeInput?.(clickEvent, code);
        },
        [clickEvent],
    );

    const impressionFaqButton = useCallback(() => {
        impressionFaqButtonEvent?.(impressionEvent);
    }, [impressionEvent]);

    const impressionTncButton = useCallback(() => {
        impressionTncButtonEvent?.(impressionEvent);
    }, [impressionEvent]);

    const isButtonDisabled = isEmpty(couponCode);

    return (
        <>
            <MastHead onBackButtonClick={onBackButtonClick} impressionMastHead={impressionEventForMastHead} />
            <Content>
                <TopContentContainer>
                    <Heading variant="V2.heading1.neutral.black" color="text_Highest_Emphasis">
                        {HOME_TITLE}
                    </Heading>
                    <SubHeading
                        variant="V2.body2.neutral.regular"
                        color="text_Med_Emphasis"
                        dangerouslySetInnerHTML={{ __html: HOME_SUBTITLE }}
                    />
                    <ApplyCouponInputBox
                        couponCode={couponCode}
                        setCouponCode={setCouponCode}
                        errorText={inputBoxError}
                        isDisabled={false}
                        setErrorText={setInputBoxError}
                        impressionCouponCodeInput={impressionCouponCodeInputAnalytics}
                        clickCouponCodeInput={clickCouponCodeInputAnalytics}
                    />
                    <ApplyCouponButton
                        variant="primary"
                        onClick={applyCouponCodeButtonClick}
                        isDisabled={isButtonDisabled}
                        data-testid={HOME_TEST_IDS.APPLY_COUPON_BTN}
                    >
                        {APPLY_COUPON_BTN}
                    </ApplyCouponButton>
                </TopContentContainer>
                <Seperator />
                <FaqContainer
                    onFaqClick={onFaqClick}
                    onTncClick={onTncClick}
                    impressionFaqButton={impressionFaqButton}
                    impressionTncButton={impressionTncButton}
                />
                {showBottomSheet && (
                    <CouponApplyingBottomSheet
                        impressionApplyingCouponLoaderEvent={impressionEventForApplyingBottomsheetLoader}
                    />
                )}
            </Content>
        </>
    );
};

export default memo(SuccessContentHomePage);
